import { z } from "zod";
export const PROTOCOL_VERSION = "0.1.0";
/** Lifecycle states for a render job leased from PostgreSQL. */
export const JobState = z.enum([
    "queued",
    "leased",
    "running",
    "succeeded",
    "failed",
    "cancelled",
]);
export const Measurements = z
    .object({
    truePeak: z.number().finite(),
    samplePeak: z.number().finite(),
    rms: z.number().finite(),
    dc: z.number().finite(),
    silence: z.boolean(),
    divergent: z.boolean(),
    nonFinite: z.boolean(),
    renderMs: z.number().finite().nonnegative(),
    outputBytes: z.number().int().nonnegative(),
})
    .strict();
export const MAX_AUDITION_SECONDS = 4.9;
export const MAX_AUDITION_NOTES = 16;
export const MIN_AUDITION_DURATION_SECONDS = 0.25;
export const MIN_AUDITION_GATE_SECONDS = 0.04;
export const MIN_AUDITION_MIDI = 24;
export const MAX_AUDITION_MIDI = 96;
export const MAX_AUDITION_POLYPHONY = 4;
export const AuditionNote = z
    .object({
    midi: z.number().int().min(MIN_AUDITION_MIDI).max(MAX_AUDITION_MIDI),
    start: z.number().finite().min(0).lt(MAX_AUDITION_SECONDS),
    duration: z.number().finite().min(MIN_AUDITION_GATE_SECONDS).max(MAX_AUDITION_SECONDS),
})
    .strict();
export const AuditionPlan = z
    .object({
    notes: z.array(AuditionNote).min(1).max(MAX_AUDITION_NOTES),
    durationSeconds: z
        .number()
        .finite()
        .min(MIN_AUDITION_DURATION_SECONDS)
        .max(MAX_AUDITION_SECONDS),
})
    .strict()
    .superRefine((plan, ctx) => {
    let activeNotes = [];
    let lastStart = -Infinity;
    for (const [index, note] of plan.notes.entries()) {
        const end = note.start + note.duration;
        if (end > plan.durationSeconds + Number.EPSILON) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `note ${index} ends after durationSeconds`,
                path: ["notes", index, "duration"],
            });
        }
        if (note.start < lastStart - Number.EPSILON) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "notes must be ordered by nondecreasing start",
                path: ["notes", index, "start"],
            });
        }
        lastStart = note.start;
        activeNotes = activeNotes.filter((active) => active.end > note.start + Number.EPSILON);
        activeNotes.push({ end });
        if (activeNotes.length > MAX_AUDITION_POLYPHONY) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "no more than four notes may be active simultaneously",
                path: ["notes", index],
            });
        }
    }
});
export const auditionJsonSchema = {
    type: "object",
    additionalProperties: false,
    required: ["durationSeconds", "notes"],
    properties: {
        durationSeconds: {
            type: "number",
            minimum: MIN_AUDITION_DURATION_SECONDS,
            maximum: MAX_AUDITION_SECONDS,
        },
        notes: {
            type: "array",
            minItems: 1,
            maxItems: MAX_AUDITION_NOTES,
            items: {
                type: "object",
                additionalProperties: false,
                required: ["midi", "start", "duration"],
                properties: {
                    midi: {
                        type: "integer",
                        minimum: MIN_AUDITION_MIDI,
                        maximum: MAX_AUDITION_MIDI,
                    },
                    start: {
                        type: "number",
                        minimum: 0,
                        exclusiveMaximum: MAX_AUDITION_SECONDS,
                    },
                    duration: {
                        type: "number",
                        minimum: MIN_AUDITION_GATE_SECONDS,
                        maximum: MAX_AUDITION_SECONDS,
                    },
                },
            },
        },
    },
};
export function auditionJsonSchemaFragment() {
    return auditionJsonSchema;
}
export function clampAuditionDuration(duration) {
    return Math.min(Math.max(duration, MIN_AUDITION_DURATION_SECONDS), MAX_AUDITION_SECONDS);
}
function clampAuditionNote(raw) {
    if (!raw || typeof raw !== "object")
        return null;
    const note = raw;
    if (typeof note.midi !== "number" ||
        !Number.isFinite(note.midi) ||
        typeof note.start !== "number" ||
        !Number.isFinite(note.start) ||
        typeof note.duration !== "number" ||
        !Number.isFinite(note.duration)) {
        return null;
    }
    const midi = Math.round(Math.min(MAX_AUDITION_MIDI, Math.max(MIN_AUDITION_MIDI, note.midi)));
    const start = Math.min(Math.max(0, note.start), MAX_AUDITION_SECONDS - MIN_AUDITION_GATE_SECONDS);
    const duration = Math.min(Math.max(MIN_AUDITION_GATE_SECONDS, note.duration), MAX_AUDITION_SECONDS);
    return { midi, start, duration };
}
/** Best-effort repair for common model timing mistakes before strict validation. */
export function repairAuditionInput(raw) {
    if (!raw || typeof raw !== "object")
        return raw;
    const input = raw;
    if (!Array.isArray(input.notes))
        return raw;
    const notes = input.notes
        .map((note) => clampAuditionNote(note))
        .filter((note) => note !== null)
        .slice(0, MAX_AUDITION_NOTES)
        .sort((a, b) => a.start - b.start);
    if (notes.length === 0)
        return raw;
    let durationSeconds = typeof input.durationSeconds === "number" && Number.isFinite(input.durationSeconds)
        ? clampAuditionDuration(input.durationSeconds)
        : clampAuditionDuration(Math.max(...notes.map((note) => note.start + note.duration)));
    const maxEnd = Math.max(...notes.map((note) => note.start + note.duration));
    durationSeconds = clampAuditionDuration(Math.max(durationSeconds, maxEnd));
    for (const note of notes) {
        const maxDuration = durationSeconds - note.start;
        if (maxDuration < MIN_AUDITION_GATE_SECONDS) {
            note.start = Math.max(0, durationSeconds - MIN_AUDITION_GATE_SECONDS);
            note.duration = MIN_AUDITION_GATE_SECONDS;
            continue;
        }
        note.duration = Math.min(note.duration, maxDuration);
    }
    for (let index = 0; index < notes.length; index++) {
        const note = notes[index];
        let active = notes
            .slice(0, index)
            .filter((other) => other.start + other.duration > note.start + Number.EPSILON);
        while (active.length >= MAX_AUDITION_POLYPHONY) {
            const shortest = active.reduce((left, right) => left.start + left.duration <= right.start + right.duration ? left : right);
            shortest.duration = Math.max(MIN_AUDITION_GATE_SECONDS, note.start - shortest.start + Number.EPSILON);
            active = notes
                .slice(0, index)
                .filter((other) => other.start + other.duration > note.start + Number.EPSILON);
        }
    }
    return { durationSeconds, notes };
}
export function parseAuditionPlan(raw) {
    const direct = AuditionPlan.safeParse(raw);
    if (direct.success) {
        return { plan: direct.data, repaired: false };
    }
    const repaired = repairAuditionInput(raw);
    const retry = AuditionPlan.safeParse(repaired);
    if (retry.success) {
        return { plan: retry.data, repaired: true };
    }
    throw new Error(`Provider audition validation failed: ${retry.error.message}`);
}
export const RenderJob = z
    .object({
    id: z.string().min(1),
    protocolVersion: z.literal(PROTOCOL_VERSION).or(z.string()),
    state: JobState,
    inputDigest: z.string().length(64),
    contentHash: z.string().length(64).optional(),
    tenantId: z.string().min(1),
    generationId: z.string().min(1),
    leaseOwner: z.string().nullable().optional(),
    leaseExpiresAt: z.string().datetime().nullable().optional(),
    attempt: z.number().int().nonnegative().default(0),
    idempotencyKey: z.string().min(1),
    createdAt: z.string().datetime().optional(),
    updatedAt: z.string().datetime().optional(),
})
    .strict();
export const RenderResult = z
    .object({
    jobId: z.string().min(1),
    ok: z.boolean(),
    inputDigest: z.string().length(64),
    rendererVersion: z.string().min(1),
    mode: z.enum(["native", "approximate"]),
    measurements: Measurements.optional(),
    outputObjectKey: z.string().optional(),
    errorCode: z.string().optional(),
    errorMessage: z.string().optional(),
})
    .strict();
//# sourceMappingURL=index.js.map